import { createReducer } from 'helpers/redux';
import { isAccessoryPending, formatAccessory } from 'helpers/accessories';
import {
  SELECT_PACK,
  ADD_ACCESSORY,
  REMOVE_ACCESSORY,
  SET_COLOUR,
  SET_MODEL,
  RESET_BIKE,
  RESET_ACCESSORIES,
  INCREASE_ACCESSORY_QUANTITY,
  DECREASE_ACCESSORY_QUANTITY,
  SET_CONFLICT_CHOICE,
  RESOLVE_ACCESSORY_CONFLICT,
  SET_REQUIRES_SINGLE_CHOICE,
  RESOLVE_ACCESSORIES_REQUIRED,
  CLOSE_ACCESSORY_RESOLUTION,
  REMOVE_REQUIRED_ACCESSORIES,
  REFRESH_BIKE_ACCESSORIES,
  REMOVE_PACK,
  LOAD_CONFIGURATION,
  RESOLVE_ACCESSORY_PACK_CONFLICT,
  RESOLVE_PACK_ACCESSORY_CONFLICT,
  SET_PREVIEWED_PACK,
  ADD_PACK_ACCESSORY,
  REMOVE_PACK_ACCESSORY,
  CLEAR_PREVIEWED_PACK,
  UPDATE_PACK_ACCESSORIES,
  CLOSE_REQUIRED_RESOLUTION,
  ADD_WARRANTY_PACK,
  ADD_SERVICE_PACK,
  REMOVE_SERVICE_PACK,
  REMOVE_WARRANTY_PACK,
} from './types';
import * as helpers from './helpers';

export const initialState = {
  configuration: {
    accessories: [],
    warrantyPack: null,
    servicePack: null,
    pendingAccessories: [],
    colour: null,
    requiresSingleChoice: null,
    conflictChoice: null,
    accessoryPendingRemoval: null,
    pack: {
      selected: null,
      previewed: null,
      selectedAccessories: [],
      accessories: [],
      pending: null,
      pendingRemoval: null,
    },
  },
  model: null,
};

const bikeReducer = createReducer({
  initialState,
  reducers: {
    [SET_PREVIEWED_PACK]: (state, { payload: pack }) => ({
      ...state,
      configuration: {
        ...state.configuration,
        pack: {
          ...state.configuration.pack,
          previewed: pack,
          selectedAccessories: pack.parts,
        },
      },
    }),
    [CLEAR_PREVIEWED_PACK]: state => ({
      ...state,
      configuration: {
        ...state.configuration,
        pack: {
          ...state.configuration.pack,
          previewed: null,
        },
      },
    }),
    [ADD_PACK_ACCESSORY]: (state, { payload: accessory }) => ({
      ...state,
      configuration: {
        ...state.configuration,
        pack: {
          ...state.configuration.pack,
          selectedAccessories: [...state.configuration.pack.selectedAccessories, accessory],
        },
      },
    }),
    [REMOVE_PACK_ACCESSORY]: (state, { payload: accessoryId }) => ({
      ...state,
      configuration: {
        ...state.configuration,
        pack: {
          ...state.configuration.pack,
          selectedAccessories: state.configuration.pack.selectedAccessories.filter(
            selectedAccessory => selectedAccessory.id !== accessoryId
          ),
        },
      },
    }),
    [SELECT_PACK]: (state, { payload: { pack, selectedAccessories } }) => {
      const { accessories } = state.configuration;

      const accessoryConflictIds = helpers.getAccessoryConflictIds(accessories);
      const packAccessoryIds = selectedAccessories.map(part => part.id);
      const packHasConflicts = packAccessoryIds.some(
        accessoryId =>
          accessoryConflictIds.includes(accessoryId) ||
          accessories.find(accessory => accessory.id === accessoryId && !accessory.allowMultiple)
      );

      if (pack.isLocked) {
        if (packHasConflicts) {
          // pack has conflicts so we'll set it as pending + display conflict resolution screen
          return {
            ...state,
            configuration: {
              ...state.configuration,
              pack: {
                ...state.configuration.pack,
                previewed: null,
                pending: pack,
              },
            },
          };
        }

        // no conflicts so just add locked pack
        return {
          ...state,
          configuration: {
            ...state.configuration,
            pack: {
              ...state.configuration.pack,
              selected: pack,
              previewed: null,
              accessories: selectedAccessories,
            },
          },
        };
      }

      // pack is unlocked so find any conflicts and requirements, and add to
      // pending queue
      const accessoriesAndPackAccessories = helpers.removeDuplicateAccessories([
        ...accessories,
        ...selectedAccessories,
      ]);
      const newPackState = {
        ...state,
        configuration: {
          ...state.configuration,
          pack: {
            ...state.configuration.pack,
            selected: null,
            accessories: [],
          },
        },
      };

      return selectedAccessories.reduce((prevState, accessory) => {
        if (isAccessoryPending(accessoriesAndPackAccessories, accessory)) {
          const newPendingAccessories = [...prevState.configuration.pendingAccessories, { ...accessory, pack: true }];
          const newPendingAccessoryIds = newPendingAccessories.map(pendingAccessory => pendingAccessory.id);

          return {
            ...prevState,
            configuration: {
              ...prevState.configuration,
              pendingAccessories: newPendingAccessories,
            },
          };
        }

        const selectedAccessoryIds = selectedAccessories.map(selectedAccessory => selectedAccessory.id);

        return {
          ...prevState,
          configuration: {
            ...prevState.configuration,
            accessories: prevState.configuration.accessories.filter(bikeAccessory => {
              const packHasBikeAccessory = selectedAccessoryIds.includes(bikeAccessory.id);

              return !packHasBikeAccessory || bikeAccessory.allowMultiple;
            }),
            pack: {
              ...prevState.configuration.pack,
              selected: pack,
              previewed: null,
              accessories: [...prevState.configuration.pack.accessories, accessory],
            },
          },
        };
      }, newPackState);
    },
    [REMOVE_PACK]: state => ({
      ...state,
      configuration: {
        ...state.configuration,
        pack: {
          ...state.configuration.pack,
          selected: null,
          accessories: [],
          pendingRemoval: null,
        },
      },
    }),
    [RESOLVE_PACK_ACCESSORY_CONFLICT]: state => {
      const { configuration } = state;
      const { pack, accessories, conflictChoice } = configuration;

      if (conflictChoice === 'pack') {
        const packAccessoryConflictIds = helpers
          .getPackAccessoryConflicts(pack.pending, accessories)
          .map(accessory => accessory.id);
        const accessoriesMinusConflicts = accessories.filter(
          accessory => !packAccessoryConflictIds.includes(accessory.id)
        );

        return {
          ...state,
          configuration: {
            ...configuration,
            pack: {
              ...configuration.pack,
              selected: pack.pending,
              accessories: pack.pending.parts,
              pending: null,
            },
            accessories: accessoriesMinusConflicts,
            conflictChoice: null,
            pendingAccessories: [],
          },
        };
      }

      return {
        ...state,
        configuration: {
          ...configuration,
          pack: {
            ...configuration.pack,
            pending: null,
          },
          conflictChoice: null,
          pendingAccessories: [],
        },
      };
    },
    [RESOLVE_ACCESSORY_PACK_CONFLICT]: state => {
      const { configuration } = state;
      const { accessories, pendingAccessories, conflictChoice } = configuration;
      const pendingAccessory = pendingAccessories[0];

      if (conflictChoice === 'accessory') {
        if (isAccessoryPending(accessories, pendingAccessory)) {
          // leave accessory in pendingAccessories but remove pack and choice state
          // so conflict and/or required screens show

          return {
            ...state,
            configuration: {
              ...configuration,
              pack: {
                ...configuration.pack,
                selected: null,
                pendingRemoval: null,
                accessories: [],
                selectedAccessories: [],
              },
              conflictChoice: null,
            },
          };
        }

        return {
          ...state,
          configuration: {
            ...state.configuration,
            pack: {
              ...state.configuration.pack,
              selected: null,
              pendingRemoval: null,
              accessories: [],
              selectedAccessories: [],
            },
            pendingAccessories: [],
            accessories: [...accessories, pendingAccessory].map(formatAccessory),
            conflictChoice: null,
          },
        };
      }

      return {
        ...state,
        configuration: {
          ...state.configuration,
          pack: {
            ...state.configuration.pack,
            pendingRemoval: null,
          },
          pendingAccessories: [],
          conflictChoice: null,
        },
      };
    },
    [ADD_ACCESSORY]: (state, { payload: { accessory: newAccessory } }) => {
      const { configuration } = state;
      const {
        pack: { selected: pack },
      } = configuration;

      if (pack && pack.isLocked) {
        const packAccessoryConflictIds = helpers.getAccessoryConflictIds(pack.parts);

        if (
          packAccessoryConflictIds.includes(newAccessory.id) ||
          newAccessory.conflicts.some(accessoryId => packAccessoryConflictIds.includes(accessoryId))
        ) {
          return {
            ...state,
            configuration: {
              ...state.configuration,
              pack: {
                ...state.configuration.pack,
                pendingRemoval: pack,
              },
              pendingAccessories: [newAccessory],
            },
          };
        }
      }

      const accessoriesAndPackAccessories = helpers.removeDuplicateAccessories([
        ...configuration.accessories,
        ...configuration.pack.accessories,
      ]);

      if (isAccessoryPending(accessoriesAndPackAccessories, newAccessory)) {
        return {
          ...state,
          configuration: {
            ...state.configuration,
            pendingAccessories: [...configuration.pendingAccessories, newAccessory].map(formatAccessory),
          },
        };
      }

      return {
        ...state,
        configuration: {
          ...state.configuration,
          accessories: [...configuration.accessories, newAccessory].map(formatAccessory),
        },
      };
    },
    [REMOVE_ACCESSORY]: (state, { payload: accessoryId }) => {
      const { accessories, pack } = state.configuration;
      const nonPackAccessoryToBeRemoved = accessories.find(accessory => accessory.id === accessoryId);
      const packAccessoryToBeRemoved = pack.accessories.find(accessory => accessory.id === accessoryId);
      const accessoryToBeRemoved = nonPackAccessoryToBeRemoved || packAccessoryToBeRemoved;

      if (!nonPackAccessoryToBeRemoved && packAccessoryToBeRemoved && pack.selected.isLocked) {
        return {
          ...state,
          configuration: {
            ...state.configuration,
            pack: {
              ...state.configuration.pack,
              pendingRemoval: pack.selected,
            },
            accessoryPendingRemoval: packAccessoryToBeRemoved,
          },
        };
      }

      const linkedAccessories = [...accessories, ...pack.accessories].filter(
        accessory =>
          accessory.requiresSingle.includes(accessoryToBeRemoved.id) ||
          accessory.requiresAll.includes(accessoryToBeRemoved.id) ||
          accessoryToBeRemoved.requiresSingle.includes(accessory.id) ||
          accessoryToBeRemoved.requiresAll.includes(accessory.id)
      );

      if (linkedAccessories.length) {
        return {
          ...state,
          configuration: {
            ...state.configuration,
            accessoryPendingRemoval: accessoryToBeRemoved.id,
          },
        };
      }

      // if we have more than one of the accessory, just remove 1
      if (accessoryToBeRemoved && accessoryToBeRemoved.quantity > 1) {
        return {
          ...state,
          configuration: {
            ...state.configuration,
            accessories: state.configuration.accessories.map(accessory => ({
              ...accessory,
              quantity: accessory.id === accessoryToBeRemoved.id ? accessory.quantity - 1 : accessory.quantity,
            })),
          },
        };
      }

      // if pack accessory in unlocked pack, just remove accessory
      if (packAccessoryToBeRemoved) {
        return {
          ...state,
          configuration: {
            ...state.configuration,
            pack: {
              ...state.configuration.pack,
              accessories: state.configuration.pack.accessories.filter(
                packAccessory => packAccessory.id !== packAccessoryToBeRemoved.id
              ),
            },
          },
        };
      }

      // otherwise remove accessory
      return {
        ...state,
        configuration: {
          ...state.configuration,
          accessories: state.configuration.accessories.filter(accessory => accessory.id !== accessoryToBeRemoved.id),
        },
      };
    },
    [REMOVE_REQUIRED_ACCESSORIES]: ({
      configuration: { accessoryPendingRemoval, accessories, pack, ...configuration },
      ...state
    }) => {
      const allBikeAccessories = [...accessories, ...pack.accessories];
      const accessoryToBeRemoved = allBikeAccessories.find(accessory => accessory.id === accessoryPendingRemoval);

      const getNonRequiredAccessories = accessory =>
        accessory.id !== accessoryPendingRemoval &&
        !accessoryToBeRemoved.requiresSingle.includes(accessory.id) &&
        !accessoryToBeRemoved.requiresAll.includes(accessory.id) &&
        !accessory.requiresAll.includes(accessoryPendingRemoval) &&
        !accessory.requiresSingle.includes(accessoryPendingRemoval);

      return {
        ...state,
        configuration: {
          ...configuration,
          accessoryPendingRemoval: null,
          accessories: accessories.filter(getNonRequiredAccessories),
          pack: {
            ...pack,
            accessories: pack.accessories.filter(getNonRequiredAccessories),
            selectedAccessories: pack.selectedAccessories.filter(getNonRequiredAccessories),
          },
        },
      };
    },
    [SET_COLOUR]: (state, action) => ({
      ...state,
      configuration: {
        ...state.configuration,
        colour: action.payload,
      },
    }),
    [SET_MODEL]: (state, action) => ({
      ...state,
      model: action.payload,
    }),
    [RESET_BIKE]: () => ({
      configuration: {
        pack: {
          ...initialState.configuration.pack,
        },
        accessories: [],
        pendingAccessories: [],
        colour: null,
        requiresSingleChoice: null,
        conflictChoice: null,
        accessoryPendingRemoval: null,
      },
      model: null,
    }),
    [RESET_ACCESSORIES]: state => ({
      ...state,
      configuration: {
        ...state.configuration,
        accessories: [],
      },
    }),
    [INCREASE_ACCESSORY_QUANTITY]: (state, action) => {
      const updatedAccessories = state.configuration.accessories.map(accessory => ({
        ...accessory,
        quantity: accessory.id === action.payload ? Math.min(accessory.quantity + 1, 9) : accessory.quantity,
      }));

      return {
        ...state,
        configuration: {
          ...state.configuration,
          accessories: updatedAccessories,
        },
      };
    },
    [DECREASE_ACCESSORY_QUANTITY]: (state, action) => {
      const updatedAccessories = state.configuration.accessories.map(accessory => ({
        ...accessory,
        quantity: accessory.id === action.payload ? accessory.quantity - 1 : accessory.quantity,
      }));

      return {
        ...state,
        configuration: {
          ...state.configuration,
          accessories: updatedAccessories,
        },
      };
    },
    [SET_CONFLICT_CHOICE]: (state, action) => ({
      ...state,
      configuration: {
        ...state.configuration,
        conflictChoice: action.payload,
      },
    }),
    [RESOLVE_ACCESSORY_CONFLICT]: state => {
      const { configuration } = state;
      const { accessories: bikeAccessories, pendingAccessories, pack } = configuration;

      // existing (conflicting) accessories list
      const existingAccessories = helpers.getConflictingAccessories(
        pendingAccessories,
        bikeAccessories,
        pack.accessories
      );
      const existingAccessoriesIds = existingAccessories.map(accessory => accessory.id);

      // new (conflicting) accessories list
      const newAccessories = helpers.getPendingConflictingAccessories(existingAccessories, pendingAccessories);
      const newAccessoriesIds = newAccessories.map(accessory => accessory.id);

      let newPendingAccessories = pendingAccessories.filter(
        pendingAccessory => !newAccessoriesIds.includes(pendingAccessory.id)
      );
      let newPackAccessories = [];

      if (configuration.conflictChoice === 'new') {
        const accessoryIds = bikeAccessories.map(accessory => accessory.id);

        const getRequiredAccessories = accessoryId => !accessoryIds.includes(accessoryId);
        const newAccessoriesConflictIds = helpers.flattenAccessoriesConflictingIds(newAccessories);
        const removeOldConflicts = accessory =>
          // the accessory isn't in the "existing" conflicts list
          !existingAccessoriesIds.includes(accessory.id) &&
          // and the accessory doesn't conflict with items in the "new"
          // conflicts list
          !accessory.conflicts.find(conflictingAccessoryId =>
            newAccessoriesConflictIds.includes(conflictingAccessoryId)
          );

        // look through requires single of all "new" accessories and get any
        // that aren't already on the bike
        const requiresSingle = helpers
          .flattenAccessoriesRequiresSingleIds(newAccessories)
          .filter(getRequiredAccessories);

        // look through requires all of all "new" accessories and get any
        // that aren't already on the bike
        const requiresAll = helpers.flattenAccessoriesRequiresAllIds(newAccessories).filter(getRequiredAccessories);

        // if accessory still has requirements, deal with conflicts and put back
        // into pending queue, unless added as part of unlocked pack (packs
        // should be added in CMS including all of their requirements)
        if ((requiresSingle.length || requiresAll.length) && !newAccessories[0].pack) {
          newPendingAccessories = [
            ...newAccessories.map(pendingAccessory => ({
              ...pendingAccessory,
              conflicts: [],
              conflictsResolved: true,
            })),
            ...newPendingAccessories,
          ];

          return {
            ...state,
            configuration: {
              ...state.configuration,
              conflictChoice: null,
              pendingAccessories: newPendingAccessories,
              accessories: bikeAccessories.filter(removeOldConflicts),
              pack: {
                ...pack,
                accessories: pack.accessories.filter(removeOldConflicts),
                selectedAccessories: pack.selectedAccessories.filter(removeOldConflicts),
              },
            },
          };
        }

        newPackAccessories = [
          ...pack.accessories.filter(removeOldConflicts),
          ...newAccessories.filter(accessory => accessory.pack),
        ];

        // If conflicts are with an unlocked pack already on the bike
        if (pack.selected) {
          // if accessory we are adding is part of an unlocked pack's accessories
          // then add to that pack
          const packPartIds = pack.selected.parts.map(accessory => accessory.id);
          const packContainsNewAccessories = packPartIds.every(accessoryId => newAccessoriesIds.includes(accessoryId));

          if (packContainsNewAccessories) {
            return {
              ...state,
              configuration: {
                ...state.configuration,
                conflictChoice: null,
                // if pending accessory is queued as part of pack accessory
                // conflict then we just add to bike so remove from queue
                pendingAccessories: newPendingAccessories,
                accessories: bikeAccessories.filter(removeOldConflicts),
                pack: {
                  ...pack,
                  accessories: newPackAccessories,
                  selectedAccessories: newPackAccessories,
                },
              },
            };
          }
        }

        // if conflicts are with a previewed pack
        if (pack.previewed) {
          return {
            ...state,
            configuration: {
              ...state.configuration,
              conflictChoice: null,
              pendingAccessories: newPendingAccessories,
              accessories: bikeAccessories.filter(removeOldConflicts),
              pack: {
                ...pack,
                // if conflict resolution leaves an empty pack, remove it from bike
                selected: newPackAccessories.length ? pack.previewed : null,
                previewed: null,
                accessories: newPackAccessories,
                selectedAccessories: newPackAccessories,
              },
            },
          };
        }

        return {
          ...state,
          configuration: {
            ...state.configuration,
            conflictChoice: null,
            pendingAccessories: newPendingAccessories,
            accessories: [
              ...bikeAccessories.filter(removeOldConflicts),
              ...newAccessories.filter(accessory => !accessory.pack),
            ],
            pack: {
              ...pack,
              // if conflict resolution leaves an empty pack, remove it from bike
              selected: newPackAccessories.length ? pack.selected : null,
              previewed: null,
              accessories: newPackAccessories,
              selectedAccessories: newPackAccessories,
            },
          },
        };
      }

      newPackAccessories = pack.accessories.filter(accessory => !newAccessoriesIds.includes(accessory.id));

      return {
        ...state,
        configuration: {
          ...state.configuration,
          conflictChoice: null,
          pendingAccessories: newPendingAccessories,
          accessories: bikeAccessories,
          pack: {
            ...pack,
            accessories: newPackAccessories,
            selectedAccessories: newPackAccessories,
          },
        },
      };
    },
    [RESOLVE_ACCESSORIES_REQUIRED]: (state, { payload: { accessories } }) => {
      const { configuration } = state;
      const resetChoiceState = {
        requiresSingleChoice: null,
      };
      const { requiresSingleChoice, pack, pendingAccessories } = configuration;
      const pendingAccessory = pendingAccessories[0];
      let furtherPendingAccessories = [];
      let newAccessories = [];
      const mapAccessoryIdToAccessory = accessoryId => accessories.find(accessory => accessory.id === accessoryId);
      const requiresAll = pendingAccessory.requiresAll.map(mapAccessoryIdToAccessory);
      const requiresSingle = pendingAccessory.requiresSingle.map(mapAccessoryIdToAccessory);

      if (!pendingAccessories.length) {
        return {
          ...state,
          configuration: {
            ...state.configuration,
            ...resetChoiceState,
          },
        };
      }

      // if pending accessory added from pack, assume all pending accessories are
      // from the pack and that conflicts have been dealt with already for these
      // accessories, and that the pack should include any requirements, so just
      // add pack accessories and clear pending
      if (pendingAccessory.pack) {
        const packAccessoriesAddedIds = pack.accessories.map(accessory => accessory.id);
        const packSelectedAccessoryIds = pack.selected.parts.map(accessory => accessory.id);
        const packRequiresSingle =
          pendingAccessory.requiresSingle
            .filter(
              requiresSingleId =>
                pack.selected.parts.includes(requiresSingleId) && !packAccessoriesAddedIds.includes(requiresSingleId)
            )
            .map(mapAccessoryIdToAccessory)[0] || [];
        const packRequiresAll = pendingAccessory.requiresAll
          .filter(
            requiresAllId =>
              packSelectedAccessoryIds.includes(requiresAllId) && !packAccessoriesAddedIds.includes(requiresAllId)
          )
          .map(mapAccessoryIdToAccessory);

        const packAccessories = [...pack.accessories, ...pendingAccessories, ...packRequiresSingle, ...packRequiresAll];

        return {
          ...state,
          configuration: {
            ...configuration,
            pendingAccessories: [],
            pack: {
              ...pack,
              accessories: packAccessories,
              selectedAccessories: packAccessories,
            },
          },
        };
      }

      if (requiresSingleChoice) {
        newAccessories.push(requiresSingleChoice);
      }

      if (pendingAccessory.requiresAll.length) {
        newAccessories = [...newAccessories, ...pendingAccessory.requiresAll];
      }

      if (requiresSingleChoice || pendingAccessory.requiresAll.length) {
        newAccessories = [...newAccessories, pendingAccessory.id];
      }

      if (requiresSingleChoice || pendingAccessory.requiresAll.length) {
        const accessoryIds = [...configuration.accessories.map(accessory => accessory.id), ...newAccessories];

        furtherPendingAccessories = [
          // ensure every accessory in the pendingAccessory's requiresAll
          // exists on the current bike configuration and that it doesn't
          // conflict with other accessories, otherwise add the missing
          // accessory to pending queue for later resolution
          ...requiresAll
            .filter(
              requiredAccessory =>
                !requiredAccessory.requiresAll.every(accessoryId => accessoryIds.includes(accessoryId)) ||
                requiredAccessory.conflicts.some(accessoryId => accessoryIds.includes(accessoryId))
            )
            // filter out any requires all accessories that conflict with each other (content issue)
            .filter((requiredAccessory, index, prevRequiresAll) => {
              const prevRequiresAllIds = prevRequiresAll.map(acc => acc.id);

              return !prevRequiresAllIds.find(accId => requiredAccessory.conflicts.includes(accId));
            }),
          // if there isn't at least 1 of the requires single already on the
          // bikes configuration, then add accessory to the end of the
          // pending queue for later resolution
          ...requiresSingle.filter(
            requiredAccessory =>
              requiredAccessory.requiresSingle.length &&
              !requiredAccessory.requiresSingle.some(accessoryId => accessoryIds.includes(accessoryId))
          ),
        ];
      }

      // populate accessory objects and not just their IDs, and ensure that any
      // pending accessories aren't saved onto the bike
      newAccessories = newAccessories
        .map(mapAccessoryIdToAccessory)
        .filter(
          accessory =>
            !furtherPendingAccessories.find(furtherPendingAccessory => furtherPendingAccessory.id === accessory.id) &&
            !configuration.accessories.find(bikeAccessory => bikeAccessory.id === accessory.id)
        );

      // if any of the new accessories are part of an unlocked pack then we want
      // to add them to packAccessories, not accessories.
      let newPackAccessories = [];
      if (configuration.pack.selected && !configuration.pack.selected.isLocked) {
        const packAccessoryIds = configuration.pack.selected.parts.map(accessory => accessory.id);
        newPackAccessories = newAccessories.filter(accessory => packAccessoryIds.includes(accessory.id));
        newAccessories = newAccessories.filter(accessory => !packAccessoryIds.includes(accessory.id));
      }

      const allNewPackAccessories = [...state.configuration.pack.accessories, ...newPackAccessories];

      return {
        ...state,
        configuration: {
          ...state.configuration,
          ...resetChoiceState,
          accessories: [...state.configuration.accessories, ...newAccessories].map(formatAccessory),
          pendingAccessories: [
            // remove first pending accessory from the pending queue
            ...state.configuration.pendingAccessories.filter(accessory => accessory.id !== pendingAccessory.id),
            ...furtherPendingAccessories,
          ].map(formatAccessory),
          pack: {
            ...state.configuration.pack,
            selected: allNewPackAccessories.length ? state.configuration.pack.selected : null,
            accessories: [...state.configuration.pack.accessories, ...newPackAccessories],
            selectedAccessories: [...state.configuration.pack.selectedAccessories, ...newPackAccessories],
          },
        },
      };
    },
    [CLOSE_ACCESSORY_RESOLUTION]: state => ({
      ...state,
      configuration: {
        ...state.configuration,
        pendingAccessories: [],
        requiresSingleChoice: null,
        conflictChoice: null,
        accessoryPendingRemoval: null,
        pack: {
          ...state.configuration.pack,
          pending: null,
          pendingRemoval: null,
          selectedAccessories: state.configuration.pack.accessories,
        },
      },
    }),
    [CLOSE_REQUIRED_RESOLUTION]: state => {
      const { configuration } = state;

      const bikeAccessoryIds = configuration.accessories.map(accessory => accessory.id);

      const newPendingAccessories = configuration.pendingAccessories
        .map(accessory => ({
          ...accessory,
          requiresSingle: [],
          requiresAll: [],
        }))
        .filter(
          accessory =>
            accessory.conflicts.length &&
            accessory.conflicts.some(accessoryId => bikeAccessoryIds.includes(accessoryId))
        );

      return {
        ...state,
        configuration: {
          ...state.configuration,
          pendingAccessories: newPendingAccessories,
          requiresSingleChoice: null,
          conflictChoice: null,
          accessoryPendingRemoval: null,
          pack: {
            ...state.configuration.pack,
            pending: null,
            pendingRemoval: null,
          },
        },
      };
    },
    [SET_REQUIRES_SINGLE_CHOICE]: (state, action) => ({
      ...state,
      configuration: {
        ...state.configuration,
        requiresSingleChoice: action.payload,
      },
    }),
    [REFRESH_BIKE_ACCESSORIES]: (state, { payload: accessories }) => ({
      ...state,
      configuration: {
        ...state.configuration,
        accessories: state.configuration.accessories
          .map(bikeAccessory => ({
            ...accessories.find(accessory => accessory.id === bikeAccessory.id),
            quantity: bikeAccessory.quantity,
          }))
          .filter(bikeAccessory => bikeAccessory.id),
      },
    }),
    [LOAD_CONFIGURATION]: (state, { payload: { packs, accessories, configuration } }) => {
      const selectedPack = packs.find(pack => pack.id === configuration.packs[0]) || null;
      const packPartIds = (selectedPack && selectedPack.parts.map(accessory => accessory.id)) || [];

      const bikeAccessories = configuration?.accessories
        .map(accessoryId => accessories.find(accessory => accessory.id === accessoryId))
        .filter(accessory => accessory);

      // TODO: Quantity support
      const newAccessories = bikeAccessories.filter(accessory => !packPartIds.includes(accessory.id));
      const newPackAccessories =
        selectedPack && selectedPack.isLocked
          ? selectedPack.parts
          : bikeAccessories.filter(accessory => packPartIds.includes(accessory.id));

      return {
        ...state,
        configuration: {
          ...state.configuration,
          accessories: newAccessories.map(formatAccessory),
          pack: {
            ...state.configuration.pack,
            selected: selectedPack,
            accessories: newPackAccessories,
            selectedAccessories: newPackAccessories,
          },
          colour: configuration.colourId,
          warrantyPack: configuration.extras?.warrantyPacks?.[0] || null,
          servicePack: configuration.extras?.servicingPacks?.[0] || null,
        },
      };
    },
    [UPDATE_PACK_ACCESSORIES]: (state, { payload: accessories }) => ({
      ...state,
      configuration: {
        ...state.configuration,
        pack: {
          ...state.configuration.pack,
          accessories: state.configuration.pack.accessories
            .map(accessory => accessories.find(newAccessory => accessory.id === newAccessory.id))
            .filter(accessory => accessory),
        },
      },
    }),
    [ADD_WARRANTY_PACK]: (state, { payload: warrantyPack }) => ({
      ...state,
      configuration: {
        ...state.configuration,
        warrantyPack,
      },
    }),
    [REMOVE_WARRANTY_PACK]: state => ({
      ...state,
      configuration: {
        ...state.configuration,
        warrantyPack: null,
      },
    }),
    [ADD_SERVICE_PACK]: (state, { payload: servicePack }) => ({
      ...state,
      configuration: {
        ...state.configuration,
        servicePack,
      },
    }),
    [REMOVE_SERVICE_PACK]: state => ({
      ...state,
      configuration: {
        ...state.configuration,
        servicePack: null,
      },
    }),
  },
});

export default bikeReducer;
